import {AssetType} from '@sollapay/enums';

import {AssetTypeDescriptorNotFoundError} from '../errors/trust-type.errors';

import type {
  AssetCodeSegment,
  AssetFieldKey,
  AssetFieldSpec,
  AssetTitleCandidate,
  AssetTypeDescriptor
} from './asset-type.descriptor';

const req = (key: AssetFieldKey, labelToken: string = key): AssetFieldSpec => ({
  key,
  labelToken,
  required: true
});
const opt = (key: AssetFieldKey, labelToken: string = key): AssetFieldSpec => ({
  key,
  labelToken,
  required: false
});

/** גוש / חלקה / תת-חלקה — a deal may be documented by address, by parcel, or by both. */
const LAND_REGISTRY: AssetFieldSpec[] = [opt('block'), opt('parcel'), opt('subParcel')];

/** Derived, so the triple is declared once and a fourth field reaches both uses. */
const LAND_REGISTRY_CODE_SEGMENTS: AssetCodeSegment[] = LAND_REGISTRY.map(({key}) => ({key}));

const APARTMENT_NUMBER = opt('unitNumber', 'apartmentNumber');
const COMMERCIAL_UNIT_NUMBER = opt('unitNumber', 'commercialUnitNumber');

/** Where a locatable asset is, else which parcel it sits on — how a deal document identifies it. */
const GENERAL_SALE_TITLES: AssetTitleCandidate[] = [
  {source: 'address', keys: ['address']},
  // subParcel is deliberately absent — it is a distinct number the title never shows.
  {source: 'registry', keys: ['block', 'parcel']}
];

/** Every field forced optional — only DEVELOPER_UNIT (built outside this helper) ever requires one. */
function generalSaleDescriptor(
  code: string,
  ownFields: AssetFieldSpec[] = []
): AssetTypeDescriptor {
  const numbered = ownFields.find(field => field.key === 'unitNumber');

  return {
    code,
    fields: [opt('address'), ...ownFields, ...LAND_REGISTRY].map(field => ({
      ...field,
      required: false
    })),
    titleCandidates: GENERAL_SALE_TITLES,
    codeSegments: numbered ? [{key: numbered.key}] : [...LAND_REGISTRY_CODE_SEGMENTS],
    hasTypeName: true
  };
}

/** Shared by LAND and OTHER — same address/area/registry fields, no unit number. */
const LAND_LIKE_DESCRIPTOR = generalSaleDescriptor('LND', [opt('area')]);

/** Exhaustive Record — a missing entry is a compile error, so no call site branches on the type. */
export const ASSET_TYPE_DESCRIPTORS: Record<AssetType, AssetTypeDescriptor> = {
  // Outside generalSaleDescriptor — B{building}-U{unit} is baked into live ledger balance names, never change it.
  [AssetType.DEVELOPER_UNIT]: {
    fields: [req('buildingNumber', 'building'), req('unitNumber', 'unit')],
    titleCandidates: [{source: 'developerUnit', keys: ['buildingNumber', 'unitNumber']}],
    codeSegments: [
      {key: 'buildingNumber', prefix: 'B'},
      {key: 'unitNumber', prefix: 'U'}
    ],
    hasTypeName: false
  },
  [AssetType.APARTMENT]: generalSaleDescriptor('APT', [APARTMENT_NUMBER, opt('floor')]),
  [AssetType.PENTHOUSE]: generalSaleDescriptor('PNT', [APARTMENT_NUMBER, opt('floor')]),
  [AssetType.GARDEN_APARTMENT]: generalSaleDescriptor('GDN', [APARTMENT_NUMBER]),
  [AssetType.DUPLEX]: generalSaleDescriptor('DPX', [APARTMENT_NUMBER]),
  [AssetType.STUDIO]: generalSaleDescriptor('STD', [APARTMENT_NUMBER]),
  [AssetType.HOUSE]: generalSaleDescriptor('HSE'),
  [AssetType.COTTAGE]: generalSaleDescriptor('COT'),
  [AssetType.VILLA]: generalSaleDescriptor('VLA'),
  [AssetType.BUILDING]: generalSaleDescriptor('BLD'),
  [AssetType.LAND]: LAND_LIKE_DESCRIPTOR,
  [AssetType.LOT]: generalSaleDescriptor('LOT'),
  [AssetType.COMMERCIAL]: generalSaleDescriptor('COM', [COMMERCIAL_UNIT_NUMBER, opt('floor')]),
  [AssetType.OFFICE]: generalSaleDescriptor('OFC', [
    opt('unitNumber', 'officeNumber'),
    opt('floor')
  ]),
  [AssetType.STORE]: generalSaleDescriptor('STR', [COMMERCIAL_UNIT_NUMBER]),
  [AssetType.INDUSTRIAL]: generalSaleDescriptor('IND'),
  [AssetType.WAREHOUSE]: generalSaleDescriptor('WRH', [COMMERCIAL_UNIT_NUMBER]),
  [AssetType.PARKING]: generalSaleDescriptor('PRK', [opt('unitNumber', 'parkingNumber')]),
  [AssetType.STORAGE]: generalSaleDescriptor('STG', [opt('unitNumber', 'storageNumber')]),
  // Same location fields as land, plus a free-text description for a category none of the
  // presets cover.
  [AssetType.OTHER]: {
    ...LAND_LIKE_DESCRIPTOR,
    code: 'OTH',
    fields: [...LAND_LIKE_DESCRIPTOR.fields, opt('description')],
    titleCandidates: [...GENERAL_SALE_TITLES, {source: 'description', keys: ['description']}]
  }
};

/** A stale browser bundle can see an assetType it doesn't recognize yet — this must not throw. */
export function findAssetTypeDescriptor(assetType: AssetType): AssetTypeDescriptor | undefined {
  return ASSET_TYPE_DESCRIPTORS[assetType];
}

export function getAssetTypeDescriptor(assetType: AssetType): AssetTypeDescriptor {
  const descriptor = findAssetTypeDescriptor(assetType);
  if (!descriptor) {
    throw new AssetTypeDescriptorNotFoundError(assetType);
  }
  return descriptor;
}

/** The attribute keys a type accepts — anything outside this set must stay null. */
export function getAssetFieldKeys(assetType: AssetType): AssetFieldKey[] {
  return getAssetTypeDescriptor(assetType).fields.map(f => f.key);
}

/** False for an unrecognized type, so callers fall back rather than guess a name. */
export function hasAssetTypeName(assetType: AssetType): boolean {
  return findAssetTypeDescriptor(assetType)?.hasTypeName ?? false;
}
